import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AnimatedLoader,
    ErrorAlert,
    HorizontalFlex,
    icons,
    Input,
    PadContainer,
    VerticalFlex,
} from 'smartsuite-ui';
import { v4 as uuidv4 } from 'uuid';
import { postDataPromise } from 'brock-fetch-utils/src/postDataPromise';
import { saveEnrollmentUri } from '../constants/apiConstants';
import AddressForm from '../components/AddressForm';
import {
    isAddressValid,
    isAlpha,
    isAlphaSpaceHyphen,
    isEmailValidFormat,
    isPhoneValid,
    isZipCodeValid,
    MAX_TAGS_PER_PASSENGER,
} from '../utils/validationUtil';
import { formatPhoneNumber } from '../utils/formatters';
import { supportedCountriesEnglish } from '../constants/countries';

function AddressPage() {
    const { t } = useTranslation();
    const { reservationId, reservationLastName } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const reservationWithUpdatedPassengers = queryClient.getQueryData([
        'reservation',
        reservationId,
        reservationLastName,
        'stateData',
    ]);
    const newAddressForm = () => ({
        id: uuidv4(),
        recipientName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: null,
        zipCode: '',
        country: supportedCountriesEnglish[0],
        numberOfTags: '',
    });
    const [addressFormData, setAddressFormData] = useState([newAddressForm()]);
    const transformedAddresses = addressFormData.map((address) => ({
        ...address,
        id: undefined,
        addressLine2: address?.addressLine2 || null,
        state: address?.state?.valueField,
        country: address?.country?.valueField,
        numberOfTags: Number(address.numberOfTags),
    }));
    const [email, setEmail] = useState('');
    const [phoneNumberField, setPhoneNumberField] = useState('');
    const phoneNumber = phoneNumberField.replace(/[^\d]/g, '');

    const numberOfPassengers =
        reservationWithUpdatedPassengers?.passengers?.filter(
            (pax) => pax.isEligible
        ).length ?? 1;
    const MAX_TAGS_TOTAL = numberOfPassengers * MAX_TAGS_PER_PASSENGER;
    const canAddAnotherAddress = addressFormData?.length < numberOfPassengers;
    const totalNumberOfTags = addressFormData.reduce((acc, curr) => {
        return acc + Number(curr.numberOfTags || 0);
    }, 0);

    const isTotalNumberOfTagsValid = totalNumberOfTags <= MAX_TAGS_TOTAL;

    const isAddressFormValid =
        addressFormData.every(
            (address) =>
                address.recipientName &&
                isAlphaSpaceHyphen(address.recipientName) &&
                address.addressLine1 &&
                isAddressValid(address.addressLine1) &&
                (!address.addressLine2 ||
                    isAddressValid(address.addressLine2)) &&
                address.city &&
                isAlpha(address.city) &&
                address.state &&
                address.zipCode &&
                isZipCodeValid(address.zipCode) &&
                address.country &&
                address.numberOfTags &&
                Number(address.numberOfTags) > 0
        ) && isTotalNumberOfTagsValid;

    // Send back to the flight details page if passengers missing
    useEffect(() => {
        if (!reservationWithUpdatedPassengers?.passengers) {
            navigate('../reservationFlightDetails');
        }
    }, [navigate, reservationWithUpdatedPassengers?.passengers]);

    const navigateToEnrollmentCard = (httpStatus) => {
        navigate('/waltdisneyworld', { state: { errorStatus: httpStatus } });
        return;
    };

    const saveEnrollmentMutation = useMutation({
        mutationFn: (payload) => postDataPromise(saveEnrollmentUri(), payload),
        onSuccess: () => {
            navigate('..');
        },
        onError: (error) => {
            navigateToEnrollmentCard(error.statusCode);
        },
    });

    function handleAddAddress() {
        setAddressFormData((prev) => [...prev, newAddressForm()]);
    }

    function handleDeleteAddress(index) {
        setAddressFormData((prev) => prev.filter((_, i) => i !== index));
    }

    function handleAddressChange(field, value, index) {
        setAddressFormData((prev) =>
            prev.map((data, i) => {
                if (i === index) {
                    data[field] = value;
                }
                return data;
            })
        );
    }

    const handleClickBack = () => {
        navigate(`../reservationFlightDetails`);
    };

    const handleSubmit = async () => {
        saveEnrollmentMutation.mutate({
            ...reservationWithUpdatedPassengers,
            addresses: transformedAddresses,
            primaryEmail: email,
            primaryPhone: phoneNumber,
            updatedTimestamp: new Date().toISOString(),
            reservationEnrolled: true,
        });
    };

    if (!reservationId) {
        return (
            <ErrorAlert type="error">
                <strong className="error-title">
                    {t('reservationNumberInvalidHeading')}
                </strong>
            </ErrorAlert>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="address-card">
                <header className="address-card__header">
                    <div className="address-card__title">
                        {t('addressPageTitle')}
                    </div>
                </header>
                <div>
                    <p>{t('addressPageDescription')}</p>
                </div>
                <VerticalFlex>
                    {addressFormData.map((data, index) => (
                        <div key={data.id}>
                            {index > 0 && (
                                <PadContainer
                                    cssClassModifiers={['pad-small']}
                                />
                            )}
                            <AddressForm
                                data={data}
                                onChange={handleAddressChange}
                                onClick={handleDeleteAddress}
                                index={index}
                                isTotalNumberOfTagsValid={
                                    isTotalNumberOfTagsValid
                                }
                            />
                        </div>
                    ))}
                    <PadContainer cssClassModifiers={['pad-small']} />
                    <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={handleAddAddress}
                        hidden={numberOfPassengers < 2 || !canAddAnotherAddress}
                    >
                        <span className={icons.plus} />
                        <span>{t('addAnotherAddress')}</span>
                    </button>
                    <PadContainer cssClassModifiers={['pad-medium']} />
                    <div className="contact-information">
                        <div className="contact-information__title">
                            {t('contactInformationTitle')}
                        </div>
                        <p>{t('contactInformationDescription')}</p>
                        <Input
                            label={`${t('emailAddress')} *`}
                            errorMessage={`${t('emailAddress')} ${t(
                                'inputInvalid'
                            )}`}
                            id="email"
                            isValid={!email || isEmailValidFormat(email)}
                            onChange={(event) => setEmail(event.target.value)}
                            value={email}
                            required
                        />
                        <Input
                            label={`${t('phoneNumber')} *`}
                            id="phoneNumber"
                            isValid={!phoneNumber || isPhoneValid(phoneNumber)}
                            value={phoneNumberField}
                            onChange={(event) => {
                                setPhoneNumberField(
                                    formatPhoneNumber(event.target.value)
                                );
                            }}
                            errorMessage={`${t('phoneNumber')} ${t(
                                'inputInvalid'
                            )}`}
                            type="tel"
                            placeholder="(###) ###-####"
                            maxLength={14}
                            required
                        />
                    </div>
                    <p>{t('phoneNumberDisclaimer')}</p>
                    <PadContainer cssClassModifiers={['pad-small']} />
                    <div className="address-card__navigate-btns-container">
                        <button
                            type="button"
                            className="btn btn--secondary"
                            onClick={handleClickBack}
                        >
                            <span className={icons.arrowLeft} />
                            <span>Back</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--primary"
                            onClick={handleSubmit}
                            disabled={
                                !isAddressFormValid ||
                                !isEmailValidFormat(email) ||
                                !isPhoneValid(phoneNumber) ||
                                saveEnrollmentMutation.isLoading
                            }
                        >
                            {saveEnrollmentMutation.isLoading ? (
                                <HorizontalFlex
                                    cssClassModifiers={['justify-center']}
                                >
                                    <AnimatedLoader />
                                </HorizontalFlex>
                            ) : (
                                <>
                                    <span>{t('submitEnrollment')}</span>
                                    <span className={icons.arrowRight} />
                                </>
                            )}
                        </button>
                    </div>
                </VerticalFlex>
            </div>
        </div>
    );
}

export default AddressPage;
